import { useCallback, useEffect, useRef } from 'react';
import { Box, VStack } from '@chakra-ui/react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { TriangleAlert } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import ContentEmptyState from '@/modules/content/common/ContentEmptyState';
import LoopItemWithActions from '@/modules/content/loop/parts/LoopItemWithActions';
import LoopNavigation from '@/modules/content/loop/parts/LoopNavigation';
import LoopSkeleton from '@/modules/content/loop/parts/LoopSkeleton';
import { getDurationSeconds } from '@/modules/content/loop/model/contentDetails';
import useFocusedDwell from '@/hooks/content/useFocusedDwell';
import useFocusedFeed, { TFocusedFeedSource } from '@/hooks/content/useFocusedFeed';
import useLoopNavigation from '@/hooks/content/useLoopNavigation';
import { getContentByIdOptions } from '@/utils/queryOptions';
import { TContent } from '@/types/Content.types';

// The focused "loop": a vertical consumption stream that renders a different layout per
// content type. It lives inside the normal page chrome rather than taking over the
// viewport, so the app header and navigation stay available throughout.
//
// This component is layout only — position and gestures live in useLoopNavigation, and
// view telemetry in useFocusedDwell.
const FocusedLoop = ({ anchorId, source = 'focused' }: { anchorId: string; source?: TFocusedFeedSource }) => {
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  const queryClient = useQueryClient();

  // The feed seeds the detail cache for cards that carry their body, so the anchor's type
  // is often already known before the focused feed responds — enough to pick the right
  // skeleton. Undefined when it isn't, and the skeleton falls back to the reel shape.
  const anchorType = queryClient.getQueryData<TContent>(getContentByIdOptions(anchorId).queryKey)?.type;

  const { items, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useFocusedFeed(
    { anchorId },
    source,
  );

  const exitToGrid = useCallback(
    () => navigate({ to: source === 'library' ? '/creator/profile' : '/explore' }),
    [navigate, source],
  );

  const { index, current, canGoNext, canGoPrevious, goNext, goPrevious, onTouchStart, onTouchEnd } = useLoopNavigation({
    items,
    anchorId,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    onExit: exitToGrid,
  });

  const { onPlaybackStateChange, onPlaybackProgress } = useFocusedDwell(current);

  // Move focus to the item container on change so screen readers announce the new item
  // and keyboard users stay in the loop rather than falling back to the page.
  const itemRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    itemRef.current?.focus({ preventScroll: true });
  }, [index]);

  if (isLoading) {
    return (
      <VStack width={MAX_CONTAINER_WIDTH} align="stretch" paddingY="12px">
        <LoopSkeleton shape={anchorType} />
      </VStack>
    );
  }

  if (isError || items.length === 0) {
    return (
      <Box width={MAX_CONTAINER_WIDTH} paddingY="40px">
        <ContentEmptyState
          icon={TriangleAlert}
          title={isError ? 'Couldn’t load the loop' : 'Nothing to show here'}
          description={
            isError
              ? 'Something went wrong while loading this stream. Please try again.'
              : 'There’s no content to play through right now.'
          }
          actionLabel={isError ? 'Try again' : 'Back to Explore'}
          onAction={isError ? () => refetch() : exitToGrid}
        />
      </Box>
    );
  }

  const nav = (
    <LoopNavigation
      canGoPrevious={canGoPrevious}
      canGoNext={canGoNext || hasNextPage}
      onPrevious={goPrevious}
      onNext={goNext}
    />
  );

  return (
    <VStack width={MAX_CONTAINER_WIDTH} align="stretch" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* The item owns the full row — media centred, side zones flexing around it.
          No opacity treatment here: reduced opacity is the loop's hover affordance, so
          using it for loading too would make the two states indistinguishable. */}
      <Box paddingY="12px">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={current?.id ?? index}
            initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -20 }}
            transition={{ duration: prefersReducedMotion ? 0.12 : 0.25 }}
            style={{ width: '100%' }}
          >
            <Box
              ref={itemRef}
              tabIndex={-1}
              outline="none"
              width="100%"
              role="group"
              aria-roledescription="feed item"
              aria-label={current ? `${current.title}, item ${index + 1} of ${items.length}` : undefined}
            >
              {current && (
                <LoopItemWithActions
                  content={current}
                  isActive
                  nav={nav}
                  // A video only counts as viewed once it has actually played for long
                  // enough, so the player's own play/pause drives the tracker's clock.
                  onPlayStateChange={(isPlaying) =>
                    onPlaybackStateChange(current, isPlaying, getDurationSeconds(current))
                  }
                  onProgress={(currentTime, duration) => onPlaybackProgress(current, currentTime, duration)}
                />
              )}
            </Box>
          </motion.div>
        </AnimatePresence>
      </Box>
    </VStack>
  );
};

export default FocusedLoop;
